import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  LabelList,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

const DEFAULT_COLORS = [
  "#1f6f4a",
  "#18753c",
  "#2a9d6a",
  "#0b6e4f",
  "#3d87b8",
  "#8a5a1a",
  "#c47b2c",
  "#5c6b63",
  "#134d32",
] as const;

export type NbsHorizontalBarRow = {
  readonly label: string;
  readonly value: number;
  readonly tip?: string;
};

export type NbsHorizontalBarChartProps = {
  readonly data: readonly NbsHorizontalBarRow[];
  readonly unit?: string;
  readonly height?: number;
  readonly color?: string;
  /** Distinct color per bar; defaults to a nature palette when `showLegend` is on. */
  readonly colors?: readonly string[];
  /** Show a visual legend with each sub-service label and numeric value. */
  readonly showLegend?: boolean;
};

function formatValue(value: number): string {
  return new Intl.NumberFormat("fr-FR", {
    maximumFractionDigits: 2,
  }).format(value);
}

/** Enough room for long French category labels without clipping. */
function yAxisWidthForLabels(labels: readonly string[], hasTips: boolean): number {
  const longestLine = labels.reduce((max, label) => {
    const lines = wrapLabel(label, LABEL_WRAP_CHARS);
    const lineMax = lines.reduce((m, line) => Math.max(m, line.length), 0);
    return Math.max(max, lineMax);
  }, 0);
  const estimated = Math.ceil(longestLine * 7.4) + (hasTips ? 26 : 14);
  return Math.min(360, Math.max(148, estimated));
}

const LABEL_WRAP_CHARS = 28;

function wrapLabel(label: string, maxChars: number): string[] {
  if (label.length <= maxChars) return [label];
  const words = label.split(/\s+/);
  const lines: string[] = [];
  let current = "";
  for (const word of words) {
    const next = current ? `${current} ${word}` : word;
    if (next.length > maxChars && current) {
      lines.push(current);
      current = word;
    } else {
      current = next;
    }
  }
  if (current) lines.push(current);
  return lines.length > 0 ? lines : [label];
}

type CategoryTickProps = {
  readonly x?: number;
  readonly y?: number;
  readonly payload?: { value?: string };
  readonly tips: Readonly<Record<string, string>>;
};

function CategoryTick({ x = 0, y = 0, payload, tips }: CategoryTickProps) {
  const label = String(payload?.value ?? "");
  const tip = tips[label];
  const lines = wrapLabel(label, LABEL_WRAP_CHARS);
  const lineHeight = 13;
  const blockOffset = ((lines.length - 1) * lineHeight) / 2;
  return (
    <g transform={`translate(${x},${y})`}>
      <text
        x={tip ? -16 : 0}
        y={-blockOffset}
        textAnchor="end"
        fill="#161616"
        fontSize={12}
        fontWeight={600}
      >
        {lines.map((line, i) => (
          <tspan key={`${line}-${i}`} x={tip ? -16 : 0} dy={i === 0 ? 4 : lineHeight}>
            {line}
          </tspan>
        ))}
      </text>
      {tip ? (
        <g transform="translate(-6, 0)" style={{ cursor: "help" }}>
          <title>{tip}</title>
          <circle r={7.5} fill="#eef3ef" stroke="#5c6b63" strokeWidth={1} />
          <text
            textAnchor="middle"
            dominantBaseline="central"
            fontSize={10}
            fontWeight={700}
            fill="#3d4a42"
          >
            ?
          </text>
        </g>
      ) : null}
    </g>
  );
}

export function NbsHorizontalBarChart({
  data,
  unit = "%",
  height = 280,
  color = "#1f6f4a",
  colors,
  showLegend = false,
}: NbsHorizontalBarChartProps) {
  const palette = colors ?? (showLegend ? DEFAULT_COLORS : null);
  const tips = Object.fromEntries(data.filter((d) => d.tip).map((d) => [d.label, d.tip as string]));
  const rows = data.map((d) => ({ name: d.label, value: d.value, tip: d.tip }));
  const wrappedLines = data.reduce(
    (sum, row) => sum + wrapLabel(row.label, LABEL_WRAP_CHARS).length,
    0,
  );
  const chartHeight = Math.max(height, 48 + data.length * 36 + wrappedLines * 18);
  const hasTips = Object.keys(tips).length > 0;
  const yWidth = yAxisWidthForLabels(
    data.map((d) => d.label),
    hasTips,
  );
  const dense = data.length >= 6;

  return (
    <div
      className={`nbs-hbar-chart${showLegend ? " nbs-hbar-chart--with-legend" : ""}${
        dense ? " nbs-hbar-chart--dense" : ""
      }`}
    >
      <ResponsiveContainer width="100%" height={chartHeight}>
        <BarChart
          layout="vertical"
          data={rows}
          margin={{ top: 8, right: 56, left: 8, bottom: unit ? 22 : 8 }}
          barCategoryGap={dense ? "10%" : "22%"}
          barGap={4}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.08)" />
          <XAxis
            type="number"
            tick={{ fontSize: 12 }}
            tickFormatter={(value: number) => formatValue(value)}
            {...(unit
              ? {
                  label: {
                    value: unit,
                    position: "insideBottomRight" as const,
                    offset: -2,
                    fontSize: 11,
                    fill: "#5c6b63",
                  },
                }
              : {})}
          />
          <YAxis
            type="category"
            dataKey="name"
            width={yWidth}
            tick={<CategoryTick tips={tips} />}
            interval={0}
          />
          <Tooltip
            formatter={(v: number) => [
              unit ? `${formatValue(v)} ${unit}` : formatValue(v),
              "Valeur",
            ]}
          />
          <Bar dataKey="value" fill={color} radius={[0, 4, 4, 0]} maxBarSize={28}>
            {palette
              ? rows.map((entry, i) => <Cell key={entry.name} fill={palette[i % palette.length]} />)
              : null}
            <LabelList
              dataKey="value"
              position="right"
              formatter={(value: number) => formatValue(value)}
              style={{ fontSize: 11, fontWeight: 650, fill: "#134d32" }}
            />
          </Bar>
        </BarChart>
      </ResponsiveContainer>

      {showLegend && rows.length > 0 ? (
        <ul className="nbs-hbar-chart__legend" aria-label="Légende des sous-services">
          {rows.map((entry, i) => (
            <li key={entry.name} className="nbs-hbar-chart__legend-item">
              <span
                className="nbs-hbar-chart__legend-swatch"
                style={{
                  background: (palette ?? DEFAULT_COLORS)[i % (palette ?? DEFAULT_COLORS).length],
                }}
                aria-hidden="true"
              />
              <span className="nbs-hbar-chart__legend-label">
                {entry.name}
                {entry.tip ? (
                  <button
                    type="button"
                    className="nbs-hbar-chart__tip"
                    aria-label={`Précision : ${entry.name}`}
                  >
                    ?
                    <span className="nbs-hbar-chart__tip-bubble" role="tooltip">
                      {entry.tip}
                    </span>
                  </button>
                ) : null}
              </span>
              <span className="nbs-hbar-chart__legend-value">
                {formatValue(entry.value)}
                {unit ? ` ${unit}` : ""}
              </span>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
